/**
 * Страница регистрации — самостоятельная регистрация с поддержкой OAuth (VK, Telegram).
 * После регистрации пользователь перенаправляется на онбординг.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Form, Input, Typography, message, Space, Divider, Spin } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import '../authPages.css';

const { Title, Text } = Typography;

// Типы OAuth конфигурации
interface OAuthConfig {
  vk: { enabled: boolean; app_id: string };
  telegram: { enabled: boolean; bot_username: string };
}

// Расширяем window для Telegram callback
declare global {
  interface Window {
    onTelegramAuth: (user: any) => void;
  }
}

const RegisterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [vkLoading, setVkLoading] = useState(false);
  const [tgLoading, setTgLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthLogin = useAuthStore((s) => s.oauthLogin);
  const tgContainerRef = useRef<HTMLDivElement>(null);

  // Навигация после успешной авторизации/регистрации
  const navigateAfterAuth = useCallback(() => {
    const { needsOnboarding } = useAuthStore.getState();
    const redirect = searchParams.get('redirect');
    if (redirect) {
      navigate(redirect);
    } else if (needsOnboarding) {
      navigate('/onboarding');
    } else {
      navigate('/');
    }
  }, [navigate, searchParams]);

  // Обработка успешного OAuth ответа
  const handleOAuthSuccess = useCallback(async (data: any) => {
    await oauthLogin(data);
    navigateAfterAuth();
  }, [oauthLogin, navigateAfterAuth]);

  // Загрузка OAuth конфигурации при монтировании
  useEffect(() => {
    const fetchOAuthConfig = async () => {
      try {
        const { data } = await api.get('/auth/admin/oauth-config');
        setOauthConfig(data);
      } catch {
        // OAuth недоступен — показываем только email-форму
        console.warn('OAuth config unavailable');
      }
    };
    fetchOAuthConfig();
  }, []);

  // Обработка VK callback — если в URL есть ?code=
  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) return;

    const handleVkCallback = async () => {
      setVkLoading(true);
      try {
        const redirectUri = `${window.location.origin}/admin/register`;
        const { data } = await api.post('/auth/admin/vk-auth', {
          code,
          redirect_uri: redirectUri,
        });
        await handleOAuthSuccess(data);
        message.success('Регистрация через VK выполнена!');
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        message.error(detail || 'Ошибка авторизации через VK');
      } finally {
        setVkLoading(false);
        // Очищаем code из URL
        window.history.replaceState({}, '', `${window.location.origin}/admin/register`);
      }
    };

    handleVkCallback();
  }, [searchParams, handleOAuthSuccess]);

  // Инициализация Telegram Login Widget
  useEffect(() => {
    if (!oauthConfig?.telegram?.enabled || !tgContainerRef.current) return;

    // Глобальный callback для Telegram Widget
    window.onTelegramAuth = async (user: any) => {
      setTgLoading(true);
      try {
        const { data } = await api.post('/auth/admin/telegram-auth', {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          photo_url: user.photo_url,
          auth_date: user.auth_date,
          hash: user.hash,
        });
        await handleOAuthSuccess(data);
        message.success('Регистрация через Telegram выполнена!');
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        message.error(detail || 'Ошибка авторизации через Telegram');
      } finally {
        setTgLoading(false);
      }
    };

    // Вставляем Telegram Widget script
    const container = tgContainerRef.current;
    container.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', oauthConfig.telegram.bot_username);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, [oauthConfig, handleOAuthSuccess]);

  const onFinish = async (values: { name: string; email: string; password: string; confirm: string }) => {
    if (values.password !== values.confirm) {
      message.error('Пароли не совпадают');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/admin/register', {
        name: values.name,
        email: values.email,
        password: values.password,
      });
      // Сохраняем токены и состояние через store
      await oauthLogin(data);
      message.success('Регистрация выполнена!');
      navigateAfterAuth();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail === 'Пользователь с таким email уже зарегистрирован') {
        message.error('Этот email уже зарегистрирован. Попробуйте войти.');
      } else {
        message.error(detail || 'Ошибка регистрации');
      }
    } finally {
      setLoading(false);
    }
  };

  // Редирект на VK OAuth
  const handleVkLogin = () => {
    if (!oauthConfig?.vk?.enabled) return;
    const redirectUri = encodeURIComponent(`${window.location.origin}/admin/register`);
    const vkUrl = `https://oauth.vk.com/authorize?client_id=${oauthConfig.vk.app_id}&redirect_uri=${redirectUri}&display=popup&scope=4194304&response_type=code&v=5.199`;
    window.location.href = vkUrl;
  };

  // Спиннер при обработке VK callback
  if (vkLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5' }}>
        <Card style={{ width: 400, textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Авторизация через VK...</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="zhorka-auth-page">
      <Card className="zhorka-auth-card">
        <div className="zhorka-auth-brand">
          <div className="zhorka-auth-brandline"><img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" /><strong>Жорка</strong></div>
          <span className="zhorka-auth-eyebrow">Регистрация новой компании</span>
          <Title level={3}>Начнём знакомство</Title>
          <Text type="secondary">Создайте кабинет компании и настройте свою витрину.</Text>
        </div>
        <Form onFinish={onFinish} layout="vertical">
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Введите имя' }, { min: 2, message: 'Минимум 2 символа' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Ваше имя" size="large" />
          </Form.Item>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Некорректный email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Введите пароль' },
              { min: 6, message: 'Минимум 6 символов' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Пароль" size="large" />
          </Form.Item>
          <Form.Item
            name="confirm"
            rules={[{ required: true, message: 'Подтвердите пароль' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Подтвердите пароль" size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={loading}>
              Зарегистрироваться
            </Button>
          </Form.Item>
        </Form>

        {/* OAuth кнопки */}
        {oauthConfig && (oauthConfig.vk?.enabled || oauthConfig.telegram?.enabled) && (
          <>
            <Divider style={{ margin: '4px 0 16px' }}>или</Divider>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {/* Кнопка VK */}
              {oauthConfig.vk?.enabled && (
                <Button
                  block
                  size="large"
                  onClick={handleVkLogin}
                  style={{
                    background: '#4C75A3',
                    borderColor: '#4C75A3',
                    color: '#fff',
                    height: 40,
                    fontWeight: 500,
                  }}
                >
                  <span style={{ marginRight: 8 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ verticalAlign: 'middle' }}>
                      <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.189 1.367 1.258 2.182 1.814.617.42 1.085.328 1.085.328l2.175-.03s1.138-.07.599-.964c-.044-.073-.314-.661-1.618-1.869-1.366-1.265-1.183-1.06.462-3.248.998-1.33 1.398-2.143 1.273-2.49-.12-.331-.856-.244-.856-.244l-2.451.015s-.182-.025-.317.056c-.131.079-.216.263-.216.263s-.387 1.028-.903 1.903c-1.088 1.85-1.524 1.948-1.702 1.833-.413-.267-.31-1.075-.31-1.649 0-1.794.272-2.541-.53-2.735-.266-.064-.462-.107-1.144-.114-.874-.009-1.614.003-2.033.208-.28.137-.495.44-.364.458.162.022.53.099.725.363.252.341.243 1.106.243 1.106s.145 2.11-.338 2.372c-.332.18-.787-.187-1.762-1.868-.5-.86-.877-1.813-.877-1.813s-.073-.178-.203-.274c-.157-.116-.377-.153-.377-.153l-2.328.015s-.35.01-.478.161c-.114.134-.009.412-.009.412s1.82 4.258 3.882 6.403c1.889 1.967 4.034 1.838 4.034 1.838h.973z"/>
                    </svg>
                  </span>
                  Войти через VK
                </Button>
              )}

              {/* Кнопка/виджет Telegram */}
              {oauthConfig.telegram?.enabled && (
                <div style={{ position: 'relative' }}>
                  {tgLoading && (
                    <div style={{ textAlign: 'center', padding: 8 }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ marginLeft: 8 }}>Авторизация через Telegram...</Text>
                    </div>
                  )}
                  <div ref={tgContainerRef} style={{ display: 'flex', justifyContent: 'center' }} />
                </div>
              )}
            </Space>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary">Уже есть аккаунт? </Text>
          <Link to="/login">Войти</Link>
        </div>
      </Card>
    </div>
  );
};

export default RegisterPage;
